import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Grid, Row, Table } from "react-bootstrap";
import {
  createPosProduct,
  getPosBootstrap,
  getPosOtherOrders,
  getPosProducts,
  getPosReportSummary,
  receivePosInventory,
  submitPosOtherOrder,
  syncPosSale,
  updatePosProduct,
} from "api/pos/posApi";
import "./pharmacyPos.css";

const fallbackProducts = [
  {
    id: 1,
    sku: "MED-0001",
    barcode: "4790010010011",
    name: "Panadol 500mg",
    generic_name: "Paracetamol",
    category: "Pain relief",
    rack_location: "A1",
    supplier_name: "Main supplier",
    selling_price: 120,
    cost_price: 90,
    stock: 164,
    reorder_level: 30,
    expiry_date: "2027-01-20",
  },
  {
    id: 2,
    sku: "MED-0002",
    barcode: "4790010010012",
    name: "Vitamin C 1000mg",
    generic_name: "Ascorbic Acid",
    category: "Vitamins",
    rack_location: "B2",
    supplier_name: "Main supplier",
    selling_price: 600,
    cost_price: 425,
    stock: 22,
    reorder_level: 20,
    expiry_date: "2026-08-02",
  },
  {
    id: 3,
    sku: "MED-0003",
    barcode: "4790010010013",
    name: "ORS Sachet",
    generic_name: "Oral Rehydration Salt",
    category: "Hydration",
    rack_location: "C1",
    supplier_name: "Wellness supplier",
    selling_price: 150,
    cost_price: 115,
    stock: 12,
    reorder_level: 25,
    expiry_date: "2026-07-18",
  },
  {
    id: 4,
    sku: "MED-0004",
    barcode: "4790010010014",
    name: "Cetirizine 10mg",
    generic_name: "Cetirizine",
    category: "Allergy",
    rack_location: "A3",
    supplier_name: "Main supplier",
    selling_price: 90,
    cost_price: 62,
    stock: 89,
    reorder_level: 20,
    expiry_date: "2027-03-11",
  },
  {
    id: 5,
    sku: "MED-0005",
    barcode: "4790010010015",
    name: "Amoxicillin 500mg",
    generic_name: "Amoxicillin",
    category: "Antibiotic",
    rack_location: "RX1",
    supplier_name: "Licensed supplier",
    selling_price: 280,
    cost_price: 210,
    stock: 44,
    reorder_level: 20,
    expiry_date: "2026-11-05",
    is_prescription_required: true,
  },
  {
    id: 6,
    sku: "DEV-0001",
    barcode: "4790010010016",
    name: "Digital Thermometer",
    generic_name: "Device",
    category: "Devices",
    rack_location: "D1",
    supplier_name: "Device supplier",
    selling_price: 1450,
    cost_price: 1050,
    stock: 8,
    reorder_level: 5,
    expiry_date: "",
  },
];

const fallbackReport = {
  sales_count: 42,
  grand_total: 86450,
  gross_profit: 19870,
  profit_margin: 22.98,
  payments: [
    { method: "cash", total: 58450 },
    { method: "card", total: 28000 },
  ],
  top_products: [
    {
      pos_product_id: 1,
      product_name: "Panadol 500mg",
      quantity_sold: 84,
      sales_total: 10080,
      cost_total: 7560,
    },
    {
      pos_product_id: 2,
      product_name: "Vitamin C 1000mg",
      quantity_sold: 31,
      sales_total: 18600,
      cost_total: 13175,
    },
  ],
  low_stock: [
    { id: 3, name: "ORS Sachet", quantity_on_hand: 12, reorder_level: 25 },
  ],
  expiring_soon: [
    {
      id: 3,
      product_name: "ORS Sachet",
      batch_no: "ORS1",
      expiry_date: "2026-07-18",
      quantity_on_hand: 12,
    },
  ],
};

const POS_QUEUE_KEY = "posSaleQueue";
const POS_INVOICES_KEY = "posRecentInvoices";
const POS_PRODUCTS_KEY = "posPreviewProducts";

const currency = (value) =>
  `LKR ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatPaymentMethod = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const orderCustomerName = (order) =>
  (order.lead && order.lead.name) ||
  (order.address && order.address.name) ||
  order.secondary_name ||
  "Customer";

const orderCustomerMobile = (order) =>
  (order.lead && (order.lead.mobile_no || order.lead.mobile)) ||
  (order.address && order.address.mobile) ||
  order.secondary_number ||
  "-";

const addPrescriptionFile = (files, value) => {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => addPrescriptionFile(files, item));
    return;
  }

  if (typeof value === "object") {
    addPrescriptionFile(files, value.prescription_file);
    addPrescriptionFile(files, value.prescription_url);
    return;
  }

  files.push(value);
};

const prescriptionFilesForOrder = (order) => {
  const files = [];

  addPrescriptionFile(files, order.prescription_file_url);
  addPrescriptionFile(files, order.prescription_file);

  (order.prescriptiondata || []).forEach((item) => {
    addPrescriptionFile(files, item.prescription_file);
    addPrescriptionFile(files, item.prescription_url);
  });

  return Array.from(new Set(files.filter(Boolean)));
};

const prescriptionTextForOrder = (order) => {
  const detailsText = (order.prescriptiondata || [])
    .map((item) => item.prescription_text)
    .filter(Boolean)
    .join("\n");

  return [order.prescription_text, detailsText].filter(Boolean).join("\n");
};

const readLocalList = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch (error) {
    return [];
  }
};

const writeLocalList = (key, list) => {
  localStorage.setItem(key, JSON.stringify(list));
};

const cloneProducts = (list = []) =>
  list.map((product) => ({
    ...product,
    barcodes: Array.isArray(product.barcodes)
      ? product.barcodes.map((barcode) => ({ ...barcode }))
      : [],
    batches: Array.isArray(product.batches)
      ? product.batches.map((batch) => ({ ...batch }))
      : [],
  }));

const readLocalProducts = () => {
  const storedProducts = readLocalList(POS_PRODUCTS_KEY);
  return cloneProducts(
    storedProducts.length ? storedProducts : fallbackProducts,
  );
};

const getToken = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.token || user.access_token || user.api_token || "";
  } catch (error) {
    return "";
  }
};

const quantityForProduct = (product) => {
  if (product.stock !== undefined) {
    return Number(product.stock || 0);
  }

  return (product.batches || []).reduce(
    (total, batch) => total + Number(batch.quantity_on_hand || 0),
    0,
  );
};

const primaryBatchId = (product) => {
  const batch = (product.batches || []).find(
    (item) => Number(item.quantity_on_hand || 0) > 0,
  );
  return batch ? batch.id : null;
};

const medicationIdForProduct = (product) =>
  product.medication_id ||
  (product.meta && product.meta.medication_id) ||
  product.flash_medication_id ||
  null;

const productMatches = (product, query) =>
  `${product.name || ""} ${product.generic_name || ""} ${product.barcode || ""} ${
    product.sku || ""
  } ${product.category || ""}`
    .toLowerCase()
    .includes(query);

const PharmacyPos = () => {
  const [activeTab, setActiveTab] = useState("sales");
  const [search, setSearch] = useState("");
  const [inventorySearch, setInventorySearch] = useState("");
  const [products, setProducts] = useState(() => readLocalProducts());
  const [inventoryDrafts, setInventoryDrafts] = useState({});
  const [report, setReport] = useState(fallbackReport);
  const [otherOrders, setOtherOrders] = useState([]);
  const [otherOrdersMeta, setOtherOrdersMeta] = useState(null);
  const [otherOrdersLoading, setOtherOrdersLoading] = useState(false);
  const [selectedOtherOrder, setSelectedOtherOrder] = useState(null);
  const [otherOrderCart, setOtherOrderCart] = useState([]);
  const [otherOrderSearch, setOtherOrderSearch] = useState("");
  const [otherOrderSubmitting, setOtherOrderSubmitting] = useState(false);
  const [pharmacyProfile, setPharmacyProfile] = useState(null);
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [saleQty, setSaleQty] = useState(1);
  const [searchSelectionIndex, setSearchSelectionIndex] = useState(0);
  const [pendingProductId, setPendingProductId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cashReceived, setCashReceived] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [lastInvoice, setLastInvoice] = useState(null);
  const [syncStatus, setSyncStatus] = useState("Ready offline");
  const [message, setMessage] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queuedSales, setQueuedSales] = useState(() =>
    readLocalList(POS_QUEUE_KEY),
  );
  const [recentInvoices, setRecentInvoices] = useState(() =>
    readLocalList(POS_INVOICES_KEY),
  );
  const [showAddMedicine, setShowAddMedicine] = useState(false);
  const [newMedicine, setNewMedicine] = useState({
    name: "",
    generic_name: "",
    barcode: "",
    sku: "",
    category: "",
    rack_location: "",
    supplier_name: "",
    stock: 0,
    cost_price: 0,
    selling_price: 0,
    reorder_level: 0,
    batch_no: "",
    expiry_date: "",
  });
  const [reportFrom, setReportFrom] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [reportTo, setReportTo] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const scanInputRef = useRef(null);
  const saleQtyInputRef = useRef(null);
  const cashInputRef = useRef(null);
  const highlightedRowRef = useRef(null);

  const replaceProducts = (nextProducts) => {
    setProducts(cloneProducts(nextProducts));
    setInventoryDrafts({});
  };

  useEffect(() => {
    const token = getToken();

    if (!token) {
      return;
    }

    getPosBootstrap(token, {
      terminal_uuid: "flash-admin-preview",
      terminal_name: "Admin Preview",
    })
      .then((res) => {
        if (res.data && res.data.pharmacy) {
          setPharmacyProfile(res.data.pharmacy);
        }

        if (res.data && res.data.products && res.data.products.length) {
          replaceProducts(res.data.products);
          setSyncStatus("Live POS data");
        }
      })
      .catch(() => setSyncStatus("Offline-ready preview"));

    getPosProducts(token)
      .then((res) => {
        const data = res.data && res.data.data ? res.data.data : res.data;
        if (Array.isArray(data) && data.length) {
          replaceProducts(data);
        }
      })
      .catch(() => {});

    getPosReportSummary(token)
      .then((res) => {
        if (res.data) {
          setReport(res.data);
          setSyncStatus("Live POS data");
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    writeLocalList(POS_PRODUCTS_KEY, products);
  }, [products]);

  useEffect(() => {
    if (activeTab === "sales") {
      focusSearchInput();
    }
  }, [activeTab]);

  useEffect(() => {
    const updateOnlineState = () => setIsOnline(navigator.onLine);

    window.addEventListener("online", updateOnlineState);
    window.addEventListener("offline", updateOnlineState);

    return () => {
      window.removeEventListener("online", updateOnlineState);
      window.removeEventListener("offline", updateOnlineState);
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (event) => {
      if (event.key === "F2") {
        event.preventDefault();
        setActiveTab("sales");
        focusSearchInput();
      }

      if (event.key === "F4") {
        event.preventDefault();
        if (cashInputRef.current) {
          cashInputRef.current.focus();
        }
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        completeSale();
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  });

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return products;
    }
    return products.filter((product) => productMatches(product, query));
  }, [products, search]);

  const visibleProducts = filteredProducts.slice(0, 50);
  const highlightedSearchProduct =
    visibleProducts[searchSelectionIndex] || visibleProducts[0] || null;
  const activeSearchProductId =
    pendingProductId ||
    (highlightedSearchProduct ? highlightedSearchProduct.id : null);
  const hasSearchQuery = Boolean(search.trim());
  const isLiveSession = Boolean(pharmacyProfile && pharmacyProfile.id);

  const inventoryProducts = useMemo(() => {
    const query = inventorySearch.trim().toLowerCase();
    if (!query) {
      return products;
    }
    return products.filter((product) => productMatches(product, query));
  }, [inventorySearch, products]);

  const otherOrderProducts = useMemo(() => {
    const query = otherOrderSearch.trim().toLowerCase();
    if (!query) {
      return products.slice(0, 50);
    }
    return products
      .filter((product) => productMatches(product, query))
      .slice(0, 50);
  }, [otherOrderSearch, products]);

  const cartSubTotal = cart.reduce(
    (total, item) => total + Number(item.selling_price || 0) * item.qty,
    0,
  );
  const cartDiscount = Math.min(Number(discount || 0), cartSubTotal);
  const cartTotal = Math.max(cartSubTotal - cartDiscount, 0);
  const changeDue = Math.max(Number(cashReceived || 0) - cartTotal, 0);
  const queuedSalesCount = queuedSales.length;
  const customerInvoices = useMemo(() => {
    const mobile = customerMobile.trim();
    if (!mobile) {
      return [];
    }

    return recentInvoices
      .filter((invoice) =>
        String(invoice.customer_mobile || "").includes(mobile),
      )
      .slice(0, 3);
  }, [customerMobile, recentInvoices]);
  const invoicePreview = useMemo(() => {
    if (lastInvoice) {
      return { ...lastInvoice, isDraft: false };
    }

    if (!cart.length) {
      return null;
    }

    return {
      id: "DRAFT",
      customer_name: customerName,
      customer_mobile: customerMobile,
      payment_method: paymentMethod,
      cash_received: Number(cashReceived || cartTotal),
      sub_total: cartSubTotal,
      discount_total: cartDiscount,
      grand_total: cartTotal,
      sold_at: new Date().toISOString(),
      isDraft: true,
      items: cart.map((item) => ({
        product_name: item.name,
        quantity: item.qty,
        unit_price: Number(item.selling_price || 0),
        line_total: Number(item.selling_price || 0) * item.qty,
        client_event_id: `DRAFT-${item.id}`,
      })),
    };
  }, [
    cart,
    cartDiscount,
    cartSubTotal,
    cartTotal,
    cashReceived,
    customerMobile,
    customerName,
    lastInvoice,
    paymentMethod,
  ]);
  const invoiceHeading = invoicePreview
    ? invoicePreview.isDraft
      ? "Current sale"
      : invoicePreview.id
    : "";
  const isSyncingQueuedSales = syncStatus.toLowerCase().includes("syncing");

  useEffect(() => {
    if (!visibleProducts.length) {
      if (searchSelectionIndex !== 0) {
        setSearchSelectionIndex(0);
      }
      if (pendingProductId) {
        setPendingProductId(null);
      }
      return;
    }

    if (searchSelectionIndex > visibleProducts.length - 1) {
      setSearchSelectionIndex(visibleProducts.length - 1);
    }

    if (
      pendingProductId &&
      !visibleProducts.some((product) => product.id === pendingProductId)
    ) {
      setPendingProductId(null);
    }
  }, [pendingProductId, searchSelectionIndex, visibleProducts]);

  useEffect(() => {
    if (highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeSearchProductId]);

  const focusSearchInput = () => {
    setTimeout(() => {
      if (scanInputRef.current) {
        scanInputRef.current.focus();
        if (scanInputRef.current.select) {
          scanInputRef.current.select();
        }
      }
    }, 0);
  };

  const focusSaleQuantity = () => {
    setTimeout(() => {
      if (saleQtyInputRef.current) {
        saleQtyInputRef.current.focus();
        if (saleQtyInputRef.current.select) {
          saleQtyInputRef.current.select();
        }
      }
    }, 0);
  };

  const selectSearchProduct = (product) => {
    if (!product) {
      return;
    }

    setPendingProductId(product.id);
    setSaleQty(1);
    setMessage("");
    focusSaleQuantity();
  };

  const moveSearchSelection = (delta) => {
    if (!visibleProducts.length) {
      return;
    }

    setPendingProductId(null);
    setSearchSelectionIndex((current) => {
      const baseIndex = Math.max(current, 0);
      return Math.max(
        0,
        Math.min(baseIndex + delta, visibleProducts.length - 1),
      );
    });
  };

  const commitPendingProductSelection = () => {
    const pendingProduct = visibleProducts.find(
      (product) => product.id === pendingProductId,
    );

    if (!pendingProduct) {
      focusSearchInput();
      return;
    }

    addToCart(pendingProduct, saleQty);
  };

  const addToCart = (product, quantity = 1) => {
    const stock = quantityForProduct(product);
    if (stock <= 0) {
      setMessage(`${product.name} is out of stock.`);
      return;
    }

    const requestedQty = Math.max(Number(quantity || 1), 1);

    setCart((current) => {
      const existing = current.find((item) => item.id === product.id);
      if (existing) {
        if (Number(existing.qty || 0) + requestedQty > stock) {
          setMessage(`Only ${stock} available for ${product.name}.`);
        }

        return current.map((item) =>
          item.id === product.id
            ? {
                ...item,
                available_stock: stock,
                qty: Math.min(Number(item.qty || 0) + requestedQty, stock),
              }
            : item,
        );
      }

      return [
        ...current,
        {
          ...product,
          available_stock: stock,
          qty: Math.min(requestedQty, stock),
        },
      ];
    });

    setSaleQty(1);
    setSearch("");
    setPendingProductId(null);
    setMessage("");
    focusSearchInput();
  };

  const cartLineStock = (item) =>
    Number(item.available_stock || quantityForProduct(item) || item.qty || 0);

  const persistQueuedSales = (nextQueue) => {
    writeLocalList(POS_QUEUE_KEY, nextQueue);
    localStorage.setItem("posQueuedSales", String(nextQueue.length));
    setQueuedSales(nextQueue);
  };

  const persistInvoice = (invoice) => {
    const nextInvoices = [
      invoice,
      ...readLocalList(POS_INVOICES_KEY).filter(
        (item) => item.id !== invoice.id,
      ),
    ].slice(0, 25);

    writeLocalList(POS_INVOICES_KEY, nextInvoices);
    setRecentInvoices(nextInvoices);
  };

  const queueSale = (payload, status) => {
    const nextQueue = [
      ...readLocalList(POS_QUEUE_KEY).filter(
        (item) => item.client_sale_id !== payload.client_sale_id,
      ),
      payload,
    ];

    persistQueuedSales(nextQueue);
    setSyncStatus(status);
  };

  const updateCartQty = (productId, qty) => {
    const nextQty = Math.max(Number(qty || 0), 0);
    setCart((current) =>
      current
        .map((item) => {
          if (item.id !== productId) {
            return item;
          }

          const stock = cartLineStock(item);
          return { ...item, qty: Math.min(nextQty, stock) };
        })
        .filter((item) => item.qty > 0),
    );
  };

  const changeCartQty = (productId, delta) => {
    setCart((current) =>
      current
        .map((item) => {
          if (item.id !== productId) {
            return item;
          }

          const stock = cartLineStock(item);
          const nextQty = Math.max(Number(item.qty || 0) + delta, 0);
          return { ...item, qty: Math.min(nextQty, stock) };
        })
        .filter((item) => item.qty > 0),
    );
  };

  const syncQueuedSales = async () => {
    const token = getToken();
    const pendingSales = readLocalList(POS_QUEUE_KEY);

    if (!pendingSales.length) {
      setSyncStatus("No queued sales");
      setMessage("");
      return;
    }

    if (!token) {
      setMessage(
        "POS access is not linked yet. Queued sales are safe on this device.",
      );
      return;
    }

    const failed = [];
    setSyncStatus(`Syncing ${pendingSales.length} queued sale(s)`);

    for (const sale of pendingSales) {
      try {
        await syncPosSale(token, sale);
      } catch (error) {
        failed.push(sale);
      }
    }

    persistQueuedSales(failed);
    setSyncStatus(
      failed.length
        ? `${failed.length} sale(s) still queued`
        : "All queued sales synced",
    );
  };

  useEffect(() => {
    if (isOnline && queuedSales.length) {
      syncQueuedSales();
    }
  }, [isOnline, queuedSales.length]);

  const loadReport = () => {
    const token = getToken();
    if (!token) {
      setMessage("Report is showing preview data until POS access is linked.");
      return;
    }

    getPosReportSummary(token, {
      from: `${reportFrom} 00:00:00`,
      to: `${reportTo} 23:59:59`,
    })
      .then((res) => {
        if (res.data) {
          setReport(res.data);
          setSyncStatus("Report refreshed");
          setMessage("");
        }
      })
      .catch(() =>
        setMessage("Could not load live report. Preview data is still shown."),
      );
  };

  const loadOtherOrders = () => {
    const token = getToken();
    if (!token) {
      setMessage("POS access is not linked yet.");
      return;
    }

    setOtherOrdersLoading(true);
    getPosOtherOrders(token)
      .then((res) => {
        const payload = res.data || {};
        const orders = payload.orders || {};
        const nextOrders = Array.isArray(orders.data) ? orders.data : [];
        setOtherOrders(nextOrders);
        if (selectedOtherOrder) {
          setSelectedOtherOrder(
            nextOrders.find((order) => order.id === selectedOtherOrder.id) ||
              selectedOtherOrder,
          );
        }
        setOtherOrdersMeta({
          district: payload.district,
          total: orders.total || 0,
        });
        setMessage("");
      })
      .catch(() =>
        setMessage("Could not load district orders for this pharmacy."),
      )
      .finally(() => setOtherOrdersLoading(false));
  };

  const selectOtherOrder = (order) => {
    setSelectedOtherOrder(order);
    setOtherOrderCart(
      (order.orderitem || []).map((item) => ({
        id: item.medication_id,
        medication_id: item.medication_id,
        name:
          (item.medication && item.medication.title) ||
          item.product_name ||
          `Medicine ${item.medication_id}`,
        quantity: Number(item.quantity || 1),
        unit_price: Number(item.unit_price || 0),
      })),
    );
    setOtherOrderSearch("");
    setMessage("");
  };

  const addProductToOtherOrder = (product) => {
    const medicationId = medicationIdForProduct(product);
    if (!medicationId) {
      setMessage("This POS product is not linked to a Flash medicine.");
      return;
    }

    setOtherOrderCart((current) => {
      const existing = current.find(
        (item) => item.medication_id === medicationId,
      );
      if (existing) {
        return current.map((item) =>
          item.medication_id === medicationId
            ? { ...item, quantity: Number(item.quantity || 0) + 1 }
            : item,
        );
      }

      return [
        ...current,
        {
          id: medicationId,
          medication_id: medicationId,
          name: product.name,
          quantity: 1,
          unit_price: Number(product.selling_price || 0),
        },
      ];
    });
    setMessage("");
  };

  const updateOtherOrderItem = (medicationId, field, value) => {
    setOtherOrderCart((current) =>
      current
        .map((item) =>
          item.medication_id === medicationId
            ? {
                ...item,
                [field]:
                  field === "quantity"
                    ? Math.max(Number(value || 0), 0)
                    : Number(value || 0),
              }
            : item,
        )
        .filter((item) => Number(item.quantity || 0) > 0),
    );
  };

  const submitOtherOrder = () => {
    const token = getToken();
    if (!selectedOtherOrder || !token || !otherOrderCart.length) {
      setMessage("Select an order and add at least one medicine.");
      return;
    }

    setOtherOrderSubmitting(true);
    submitPosOtherOrder(token, selectedOtherOrder.id, {
      items: otherOrderCart.map((item) => ({
        medication_id: item.medication_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
      })),
    })
      .then((res) => {
        setSelectedOtherOrder(res.data || selectedOtherOrder);
        setOtherOrderCart([]);
        setMessage("Order submitted successfully.");
        loadOtherOrders();
      })
      .catch((error) => {
        const payload = error.response && error.response.data;
        setMessage((payload && payload.message) || "Could not submit order.");
      })
      .finally(() => setOtherOrderSubmitting(false));
  };

  useEffect(() => {
    if (activeTab === "otherOrders") {
      loadOtherOrders();
    }
  }, [activeTab]);

  const refreshProductsFromServer = async (token) => {
    const res = await getPosProducts(token);
    const data = res.data && res.data.data ? res.data.data : res.data;

    if (Array.isArray(data) && data.length) {
      replaceProducts(data);
    }
  };

  const handleNewMedicineChange = (field, value) => {
    setNewMedicine((current) => ({
      ...current,
      [field]: [
        "stock",
        "cost_price",
        "selling_price",
        "reorder_level",
      ].includes(field)
        ? Number(value || 0)
        : value,
    }));
  };

  const updateInventoryDraft = (productId, field, value) => {
    setInventoryDrafts((current) => ({
      ...current,
      [productId]: {
        ...(current[productId] || {}),
        [field]: value,
      },
    }));
  };

  const clearInventoryDraft = (productId) => {
    setInventoryDrafts((current) => {
      if (!current[productId]) {
        return current;
      }

      const nextDrafts = { ...current };
      delete nextDrafts[productId];
      return nextDrafts;
    });
  };

  const inventoryFieldValue = (product, field, fallbackValue = "") => {
    const draft = inventoryDrafts[product.id];
    if (draft && Object.prototype.hasOwnProperty.call(draft, field)) {
      return draft[field];
    }

    return fallbackValue;
  };

  const addMedicine = () => {
    if (!newMedicine.name || !newMedicine.selling_price) {
      setMessage("Medicine name and selling price are required.");
      return;
    }

    const localProduct = {
      ...newMedicine,
      id: `local-${Date.now()}`,
      is_active: true,
    };

    setProducts((current) => [localProduct, ...current]);
    setShowAddMedicine(false);
    setNewMedicine({
      name: "",
      generic_name: "",
      barcode: "",
      sku: "",
      category: "",
      rack_location: "",
      supplier_name: "",
      stock: 0,
      cost_price: 0,
      selling_price: 0,
      reorder_level: 0,
      batch_no: "",
      expiry_date: "",
    });
    setMessage("");

    const token = getToken();
    if (!token) {
      return;
    }

    createPosProduct(token, localProduct)
      .then(async (res) => {
        if (!res.data) {
          return;
        }

        const savedProduct = res.data;
        let hydratedProduct = savedProduct;

        if (Number(localProduct.stock || 0) > 0) {
          const inventoryRes = await receivePosInventory(token, {
            pos_product_id: savedProduct.id,
            medication_id: medicationIdForProduct(savedProduct),
            quantity: Number(localProduct.stock || 0),
            batch_no: localProduct.batch_no || null,
            expiry_date: localProduct.expiry_date || null,
            cost_price: Number(localProduct.cost_price || 0),
            selling_price: Number(localProduct.selling_price || 0),
            movement_type: "opening_balance",
            reason: "Opening stock",
            client_event_id: `OPEN-${savedProduct.id}-${Date.now()}`,
          });

          if (inventoryRes.data && inventoryRes.data.batch) {
            hydratedProduct = {
              ...savedProduct,
              stock: Number(localProduct.stock || 0),
              expiry_date:
                inventoryRes.data.batch.expiry_date || localProduct.expiry_date,
              batches: [inventoryRes.data.batch],
            };
          }
        }

        setProducts((current) =>
          current.map((product) =>
            product.id === localProduct.id ? hydratedProduct : product,
          ),
        );
        setSyncStatus("Medicine saved");
      })
      .catch(() => setSyncStatus("Medicine pending cloud save"));
  };

  const saveInventoryProduct = async (product) => {
    const draft = inventoryDrafts[product.id];

    if (!draft) {
      return;
    }

    const currentStock = quantityForProduct(product);
    const targetStock = Math.max(
      Number(
        Object.prototype.hasOwnProperty.call(draft, "stock")
          ? draft.stock
          : currentStock,
      ) || 0,
      0,
    );
    const stockDelta = targetStock - currentStock;
    const productPayload = {
      barcode: Object.prototype.hasOwnProperty.call(draft, "barcode")
        ? draft.barcode
        : product.barcode || "",
      rack_location: Object.prototype.hasOwnProperty.call(
        draft,
        "rack_location",
      )
        ? draft.rack_location
        : product.rack_location || "",
      supplier_name: Object.prototype.hasOwnProperty.call(
        draft,
        "supplier_name",
      )
        ? draft.supplier_name
        : product.supplier_name || "",
      cost_price: Number(
        Object.prototype.hasOwnProperty.call(draft, "cost_price")
          ? draft.cost_price
          : product.cost_price || 0,
      ),
      selling_price: Number(
        Object.prototype.hasOwnProperty.call(draft, "selling_price")
          ? draft.selling_price
          : product.selling_price || 0,
      ),
      reorder_level: Number(
        Object.prototype.hasOwnProperty.call(draft, "reorder_level")
          ? draft.reorder_level
          : product.reorder_level || 0,
      ),
    };
    const localProduct = {
      ...product,
      ...productPayload,
      stock: targetStock,
    };

    setProducts((current) =>
      current.map((item) => (item.id === product.id ? localProduct : item)),
    );
    clearInventoryDraft(product.id);
    setMessage(
      stockDelta !== 0
        ? `${product.name} stock updated.`
        : `${product.name} details saved.`,
    );

    const token = getToken();
    if (!token || !isLiveSession || typeof product.id !== "number") {
      setSyncStatus("Preview inventory saved");
      return;
    }

    try {
      setSyncStatus("Saving inventory");
      await updatePosProduct(token, product.id, productPayload);

      if (stockDelta !== 0) {
        const liveBatch = (product.batches || []).find(
          (batch) => batch && batch.is_active !== false,
        );

        await receivePosInventory(token, {
          pos_product_id: product.id,
          medication_id: medicationIdForProduct(product),
          quantity: stockDelta,
          batch_no: liveBatch ? liveBatch.batch_no : null,
          expiry_date: liveBatch
            ? liveBatch.expiry_date
            : product.expiry_date || null,
          cost_price: productPayload.cost_price,
          selling_price: productPayload.selling_price,
          movement_type: stockDelta >= 0 ? "adjust_in" : "adjust_out",
          reason: "Manual stock adjustment",
          client_event_id: `ADJ-${product.id}-${Date.now()}`,
        });
      }

      await refreshProductsFromServer(token);
      setSyncStatus("Inventory saved");
    } catch (error) {
      setSyncStatus("Inventory pending cloud save");
      setMessage(
        "Inventory saved on this device. Cloud sync will complete when POS access is ready.",
      );
    }
  };

  const handleInventoryInputKeyDown = (event, product) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveInventoryProduct(product);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      clearInventoryDraft(product.id);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveSearchSelection(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveSearchSelection(-1);
      return;
    }

    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    const query = search.trim().toLowerCase();
    const exactMatch = products.find(
      (product) =>
        String(product.barcode || "").toLowerCase() === query ||
        String(product.sku || "").toLowerCase() === query,
    );
    const selectedProduct =
      (searchSelectionIndex > 0 && highlightedSearchProduct) ||
      exactMatch ||
      highlightedSearchProduct;

    if (selectedProduct) {
      selectSearchProduct(selectedProduct);
      return;
    }
  };

  const completeSale = () => {
    if (!cart.length) {
      return;
    }

    const invoice = {
      id: `POS-${Date.now()}`,
      customer_name: customerName,
      customer_mobile: customerMobile,
      payment_method: paymentMethod,
      cash_received: Number(cashReceived || cartTotal),
      sub_total: cartSubTotal,
      discount_total: cartDiscount,
      grand_total: cartTotal,
      sold_at: new Date().toISOString(),
      items: cart.map((item) => ({
        pos_product_id: item.id,
        medication_id: medicationIdForProduct(item),
        pos_inventory_batch_id: primaryBatchId(item),
        product_name: item.name,
        quantity: item.qty,
        unit_price: Number(item.selling_price || 0),
        line_total: Number(item.selling_price || 0) * item.qty,
        client_event_id: `EV-${Date.now()}-${item.id}`,
      })),
    };

    setProducts((current) =>
      current.map((product) => {
        const soldItem = cart.find((item) => item.id === product.id);
        if (!soldItem || product.stock === undefined) {
          return product;
        }
        return { ...product, stock: Math.max(product.stock - soldItem.qty, 0) };
      }),
    );

    setLastInvoice(invoice);
    persistInvoice(invoice);
    setCart([]);
    setDiscount(0);
    setCashReceived("");
    setMessage("");

    const token = getToken();
    const payload = {
      client_sale_id: invoice.id,
      customer_name: customerName,
      customer_mobile: customerMobile,
      sub_total: invoice.sub_total,
      discount_total: invoice.discount_total,
      grand_total: invoice.grand_total,
      was_offline: !navigator.onLine,
      sold_at: invoice.sold_at,
      items: invoice.items,
      payments: [
        {
          method: paymentMethod,
          amount: invoice.grand_total,
        },
      ],
    };

    if (!navigator.onLine || !token) {
      queueSale(payload, "Offline sale queued");
      return;
    }

    syncPosSale(token, payload)
      .then(() => setSyncStatus("Sale synced"))
      .catch(() => queueSale(payload, "Sale queued for retry"));
  };

  const printInvoice = () => {
    if (!invoicePreview) {
      setMessage("Add at least one medicine before printing an invoice.");
      return;
    }
    window.print();
  };

  return (
    <div className="main-content">
      <Grid fluid>
        <Row>
          <Col md={12}>
            <div className="pharmacy-pos">
              <div className="pharmacy-pos__header">
                <div>
                  <h3 className="pharmacy-pos__title">Pharmacy POS</h3>
                </div>
                <div className="pharmacy-pos__status">
                  <span
                    className={`pharmacy-pos__status-pill ${
                      isOnline ? "" : "pharmacy-pos__status-pill--offline"
                    }`}
                  >
                    {isOnline ? "Online" : "Offline"}
                  </span>
                  <span className="pharmacy-pos__status-pill">
                    {isLiveSession ? "Live" : "Preview"}
                  </span>
                  {queuedSalesCount > 0 && (
                    <span className="pharmacy-pos__status-pill pharmacy-pos__status-pill--queued">
                      {queuedSalesCount} queued
                    </span>
                  )}
                  {isSyncingQueuedSales && (
                    <span className="pharmacy-pos__status-pill">Syncing</span>
                  )}
                  {queuedSalesCount > 0 && isOnline && (
                    <button
                      className="pharmacy-pos__sync-link"
                      onClick={syncQueuedSales}
                      type="button"
                    >
                      Sync
                    </button>
                  )}
                </div>
              </div>

              <div className="pharmacy-pos__tabs">
                {["sales", "inventory", "otherOrders", "reports"].map((tab) => (
                  <button
                    className={`pharmacy-pos__tab ${
                      activeTab === tab ? "active" : ""
                    }`}
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    {tab === "otherOrders"
                      ? "Other Orders"
                      : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {message && <div className="pharmacy-pos__notice">{message}</div>}

              {activeTab === "sales" && (
                <div className="pharmacy-pos__sales-layout">
                  <div className="pharmacy-pos__panel pharmacy-pos__sell-panel">
                    <div className="pharmacy-pos__panel-header">
                      <div>
                        <div className="pharmacy-pos__panel-title">
                          Products
                        </div>
                        {hasSearchQuery && (
                          <div className="pharmacy-pos__muted">
                            {filteredProducts.length} match
                            {filteredProducts.length === 1 ? "" : "es"}
                          </div>
                        )}
                      </div>
                      <div className="pharmacy-pos__scan-row">
                        <input
                          className="pharmacy-pos__search"
                          onChange={(event) => {
                            setSearch(event.target.value);
                            setSearchSelectionIndex(0);
                            setPendingProductId(null);
                          }}
                          onKeyDown={handleSearchKeyDown}
                          placeholder="Scan barcode or search name, generic, SKU"
                          ref={scanInputRef}
                          value={search}
                        />
                        <input
                          className="pharmacy-pos__qty-input"
                          min="1"
                          onChange={(event) => setSaleQty(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              if (pendingProductId) {
                                commitPendingProductSelection();
                                return;
                              }
                              focusSearchInput();
                            }

                            if (event.key === "Escape") {
                              event.preventDefault();
                              focusSearchInput();
                            }
                          }}
                          ref={saleQtyInputRef}
                          type="number"
                          value={saleQty}
                        />
                      </div>
                    </div>

                    <div className="pharmacy-pos__product-table">
                      <Table className="pharmacy-pos__table" responsive>
                        <thead>
                          <tr>
                            <th>Medicine</th>
                            <th>Generic</th>
                            <th>SKU</th>
                            <th>Barcode</th>
                            <th>Stock</th>
                            <th>Price</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {visibleProducts.map((product, productIndex) => {
                            const stock = quantityForProduct(product);
                            return (
                              <tr
                                className={
                                  product.id === activeSearchProductId
                                    ? "pharmacy-pos__table-row--active"
                                    : ""
                                }
                                key={product.id}
                                ref={
                                  product.id === activeSearchProductId
                                    ? highlightedRowRef
                                    : null
                                }
                              >
                                <td>
                                  <strong>{product.name}</strong>
                                  {product.is_prescription_required && (
                                    <span className="pharmacy-pos__pill">
                                      Rx
                                    </span>
                                  )}
                                </td>
                                <td>{product.generic_name || "-"}</td>
                                <td>{product.sku || product.barcode || "-"}</td>
                                <td>{product.barcode || "-"}</td>
                                <td
                                  className={
                                    stock <= Number(product.reorder_level || 0)
                                      ? "pharmacy-pos__danger"
                                      : ""
                                  }
                                >
                                  {stock}
                                </td>
                                <td>{currency(product.selling_price)}</td>
                                <td>
                                  <button
                                    className="pharmacy-pos__small-button"
                                    disabled={stock <= 0}
                                    onClick={() => addToCart(product, saleQty)}
                                    onFocus={() => {
                                      setSearchSelectionIndex(productIndex);
                                      setPendingProductId(null);
                                    }}
                                    type="button"
                                  >
                                    Add
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  <div className="pharmacy-pos__panel pharmacy-pos__cart-panel">
                    <div className="pharmacy-pos__panel-header">
                      <div className="pharmacy-pos__panel-title">Cart</div>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => setCart([])}
                        type="button"
                      >
                        Clear
                      </button>
                    </div>

                    <div className="pharmacy-pos__customer-grid">
                      <input
                        onChange={(event) =>
                          setCustomerName(event.target.value)
                        }
                        placeholder="Customer name"
                        value={customerName}
                      />
                      <input
                        onChange={(event) =>
                          setCustomerMobile(event.target.value)
                        }
                        placeholder="Mobile"
                        value={customerMobile}
                      />
                    </div>

                    <div className="pharmacy-pos__cart-list">
                      {cart.length === 0 && (
                        <div className="pharmacy-pos__empty">
                          Scan or add a medicine to start
                        </div>
                      )}
                      {cart.map((item) => (
                        <div className="pharmacy-pos__cart-line" key={item.id}>
                          <div className="pharmacy-pos__cart-name">
                            <strong>{item.name}</strong>
                            <span>{currency(item.selling_price)}</span>
                            <span>Available {cartLineStock(item)}</span>
                          </div>
                          <div className="pharmacy-pos__qty">
                            <button
                              data-cart-action="minus"
                              onClick={() => changeCartQty(item.id, -1)}
                              type="button"
                            >
                              -
                            </button>
                            <input
                              onChange={(event) =>
                                updateCartQty(
                                  item.id,
                                  event.target.value.replace(/[^0-9]/g, ""),
                                )
                              }
                              inputMode="numeric"
                              type="text"
                              value={item.qty}
                            />
                            <button
                              data-cart-action="plus"
                              onClick={() => addToCart(item, 1)}
                              type="button"
                            >
                              +
                            </button>
                          </div>
                          <strong>
                            {currency(
                              Number(item.selling_price || 0) * item.qty,
                            )}
                          </strong>
                        </div>
                      ))}
                    </div>

                    <div className="pharmacy-pos__checkout">
                      <div className="pharmacy-pos__field-row">
                        <label>Discount</label>
                        <input
                          min="0"
                          onChange={(event) => setDiscount(event.target.value)}
                          type="number"
                          value={discount}
                        />
                      </div>
                      <div className="pharmacy-pos__field-row">
                        <label>Payment</label>
                        <select
                          onChange={(event) =>
                            setPaymentMethod(event.target.value)
                          }
                          value={paymentMethod}
                        >
                          <option value="cash">Cash</option>
                          <option value="card">Card</option>
                          <option value="bank_transfer">Bank transfer</option>
                        </select>
                      </div>
                      <div className="pharmacy-pos__field-row">
                        <label>Cash received</label>
                        <input
                          min="0"
                          onChange={(event) =>
                            setCashReceived(event.target.value)
                          }
                          type="number"
                          value={cashReceived}
                          ref={cashInputRef}
                        />
                      </div>
                      {customerInvoices.length > 0 && (
                        <div className="pharmacy-pos__customer-history">
                          <strong>Recent purchases</strong>
                          {customerInvoices.map((invoice) => (
                            <button
                              key={invoice.id}
                              onClick={() => setLastInvoice(invoice)}
                              type="button"
                            >
                              {invoice.id} · {currency(invoice.grand_total)}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="pharmacy-pos__total-line">
                        <span>Subtotal</span>
                        <strong>{currency(cartSubTotal)}</strong>
                      </div>
                      <div className="pharmacy-pos__total-line">
                        <span>Total</span>
                        <strong>{currency(cartTotal)}</strong>
                      </div>
                      <div className="pharmacy-pos__total-line">
                        <span>Change</span>
                        <strong>{currency(changeDue)}</strong>
                      </div>
                      <button
                        className="pharmacy-pos__button"
                        disabled={!cart.length}
                        onClick={completeSale}
                        type="button"
                      >
                        Complete Sale
                      </button>
                    </div>
                  </div>

                  <div className="pharmacy-pos__panel pharmacy-pos__invoice">
                    <div className="pharmacy-pos__panel-header">
                      <div className="pharmacy-pos__panel-title">Invoice</div>
                      <button
                        className="btn btn-default btn-sm"
                        disabled={!invoicePreview}
                        onClick={printInvoice}
                        type="button"
                      >
                        Print
                      </button>
                    </div>
                    {!invoicePreview ? (
                      <div className="pharmacy-pos__empty">No invoice yet</div>
                    ) : (
                      <div className="pharmacy-pos__receipt">
                        {pharmacyProfile && pharmacyProfile.name && (
                          <h4>{pharmacyProfile.name}</h4>
                        )}
                        <div className="pharmacy-pos__receipt-heading">
                          <strong>{invoiceHeading}</strong>
                        </div>
                        {invoicePreview.customer_name && (
                          <div>{invoicePreview.customer_name}</div>
                        )}
                        {invoicePreview.customer_mobile && (
                          <div>{invoicePreview.customer_mobile}</div>
                        )}
                        <div>
                          {new Date(invoicePreview.sold_at).toLocaleString()}
                        </div>
                        <hr />
                        {invoicePreview.items.map((item) => (
                          <div
                            className="pharmacy-pos__receipt-line"
                            key={item.client_event_id}
                          >
                            <span>
                              {item.product_name} x {item.quantity}
                            </span>
                            <strong>{currency(item.line_total)}</strong>
                          </div>
                        ))}
                        <hr />
                        <div className="pharmacy-pos__receipt-line">
                          <span>Discount</span>
                          <strong>
                            {currency(invoicePreview.discount_total)}
                          </strong>
                        </div>
                        <div className="pharmacy-pos__receipt-line">
                          <span>Total</span>
                          <strong>
                            {currency(invoicePreview.grand_total)}
                          </strong>
                        </div>
                        <div className="pharmacy-pos__receipt-line">
                          <span>Payment</span>
                          <strong>
                            {formatPaymentMethod(invoicePreview.payment_method)}
                          </strong>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "inventory" && (
                <div className="pharmacy-pos__panel">
                  <div className="pharmacy-pos__panel-header">
                    <div className="pharmacy-pos__panel-title">Inventory</div>
                    <div className="pharmacy-pos__toolbar">
                      <input
                        className="pharmacy-pos__search"
                        onChange={(event) =>
                          setInventorySearch(event.target.value)
                        }
                        placeholder="Search medicine, barcode, category"
                        value={inventorySearch}
                      />
                      <button
                        className="pharmacy-pos__small-button"
                        onClick={() => setShowAddMedicine(!showAddMedicine)}
                        type="button"
                      >
                        Add Medicine
                      </button>
                    </div>
                  </div>
                  {showAddMedicine && (
                    <div className="pharmacy-pos__add-form">
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange("name", event.target.value)
                        }
                        placeholder="Medicine name"
                        value={newMedicine.name}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "generic_name",
                            event.target.value,
                          )
                        }
                        placeholder="Generic name"
                        value={newMedicine.generic_name}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange("barcode", event.target.value)
                        }
                        placeholder="Barcode"
                        value={newMedicine.barcode}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange("sku", event.target.value)
                        }
                        placeholder="SKU"
                        value={newMedicine.sku}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "category",
                            event.target.value,
                          )
                        }
                        placeholder="Category"
                        value={newMedicine.category}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "rack_location",
                            event.target.value,
                          )
                        }
                        placeholder="Rack"
                        value={newMedicine.rack_location}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "supplier_name",
                            event.target.value,
                          )
                        }
                        placeholder="Supplier"
                        value={newMedicine.supplier_name}
                      />
                      <input
                        min="0"
                        onChange={(event) =>
                          handleNewMedicineChange("stock", event.target.value)
                        }
                        placeholder="Opening stock"
                        type="number"
                        value={newMedicine.stock}
                      />
                      <input
                        min="0"
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "cost_price",
                            event.target.value,
                          )
                        }
                        placeholder="Buying price"
                        type="number"
                        value={newMedicine.cost_price}
                      />
                      <input
                        min="0"
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "selling_price",
                            event.target.value,
                          )
                        }
                        placeholder="Selling price"
                        type="number"
                        value={newMedicine.selling_price}
                      />
                      <input
                        min="0"
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "reorder_level",
                            event.target.value,
                          )
                        }
                        placeholder="Reorder level"
                        type="number"
                        value={newMedicine.reorder_level}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "batch_no",
                            event.target.value,
                          )
                        }
                        placeholder="Opening batch"
                        value={newMedicine.batch_no}
                      />
                      <input
                        onChange={(event) =>
                          handleNewMedicineChange(
                            "expiry_date",
                            event.target.value,
                          )
                        }
                        placeholder="Expiry date"
                        type="date"
                        value={newMedicine.expiry_date}
                      />
                      <button
                        className="pharmacy-pos__button pharmacy-pos__button--compact"
                        onClick={addMedicine}
                        type="button"
                      >
                        Save Medicine
                      </button>
                    </div>
                  )}
                  <Table className="pharmacy-pos__table" responsive>
                    <thead>
                      <tr>
                        <th>Medicine</th>
                        <th>Category</th>
                        <th>Barcode</th>
                        <th>Rack</th>
                        <th>Supplier</th>
                        <th>Stock</th>
                        <th>Cost</th>
                        <th>Price</th>
                        <th>Reorder</th>
                        <th>Expiry</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryProducts.slice(0, 100).map((product) => {
                        const stock = quantityForProduct(product);
                        const displayedStock = Number(
                          inventoryFieldValue(product, "stock", stock) || 0,
                        );
                        const reorder = Number(
                          inventoryFieldValue(
                            product,
                            "reorder_level",
                            Number(product.reorder_level || 0),
                          ) || 0,
                        );
                        const isDirty = Boolean(inventoryDrafts[product.id]);
                        return (
                          <tr
                            className={
                              isDirty ? "pharmacy-pos__table-row--dirty" : ""
                            }
                            key={product.id}
                          >
                            <td>
                              <strong>{product.name}</strong>
                              <div className="pharmacy-pos__muted">
                                {product.generic_name || product.sku || "-"}
                              </div>
                            </td>
                            <td>{product.category || "-"}</td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input pharmacy-pos__cell-input--wide"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "barcode",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                value={inventoryFieldValue(
                                  product,
                                  "barcode",
                                  product.barcode || "",
                                )}
                              />
                            </td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "rack_location",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                value={inventoryFieldValue(
                                  product,
                                  "rack_location",
                                  product.rack_location || "",
                                )}
                              />
                            </td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input pharmacy-pos__cell-input--wide"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "supplier_name",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                value={inventoryFieldValue(
                                  product,
                                  "supplier_name",
                                  product.supplier_name || "",
                                )}
                              />
                            </td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input"
                                min="0"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "stock",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                type="number"
                                value={inventoryFieldValue(
                                  product,
                                  "stock",
                                  displayedStock,
                                )}
                              />
                            </td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input"
                                min="0"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "cost_price",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                type="number"
                                value={inventoryFieldValue(
                                  product,
                                  "cost_price",
                                  Number(product.cost_price || 0),
                                )}
                              />
                            </td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input"
                                min="0"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "selling_price",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                type="number"
                                value={inventoryFieldValue(
                                  product,
                                  "selling_price",
                                  Number(product.selling_price || 0),
                                )}
                              />
                            </td>
                            <td>
                              <input
                                className="pharmacy-pos__cell-input"
                                min="0"
                                onChange={(event) =>
                                  updateInventoryDraft(
                                    product.id,
                                    "reorder_level",
                                    event.target.value,
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleInventoryInputKeyDown(event, product)
                                }
                                type="number"
                                value={inventoryFieldValue(
                                  product,
                                  "reorder_level",
                                  reorder,
                                )}
                              />
                            </td>
                            <td>{product.expiry_date || "-"}</td>
                            <td>
                              {displayedStock <= reorder ? (
                                <span className="pharmacy-pos__danger">
                                  Low stock
                                </span>
                              ) : product.expiry_date &&
                                product.expiry_date < "2026-09-01" ? (
                                <span className="pharmacy-pos__warning">
                                  Expiring
                                </span>
                              ) : (
                                "Healthy"
                              )}
                            </td>
                            <td>
                              <div className="pharmacy-pos__row-actions">
                                <button
                                  className="pharmacy-pos__small-button"
                                  disabled={!isDirty}
                                  onClick={() => saveInventoryProduct(product)}
                                  type="button"
                                >
                                  Save
                                </button>
                                {isDirty && (
                                  <button
                                    className="pharmacy-pos__text-button"
                                    onClick={() =>
                                      clearInventoryDraft(product.id)
                                    }
                                    type="button"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              {activeTab === "otherOrders" && (
                <div className="pharmacy-pos__orders-workspace">
                  <div className="pharmacy-pos__panel pharmacy-pos__orders-list-panel">
                    <div className="pharmacy-pos__panel-header">
                      <div>
                        <div className="pharmacy-pos__panel-title">
                          District Orders
                        </div>
                        {otherOrdersMeta && (
                          <div className="pharmacy-pos__muted">
                            {otherOrdersMeta.total} order
                            {otherOrdersMeta.total === 1 ? "" : "s"}
                            {otherOrdersMeta.district
                              ? ` in ${otherOrdersMeta.district}`
                              : ""}
                          </div>
                        )}
                      </div>
                      <button
                        className="pharmacy-pos__small-button"
                        onClick={loadOtherOrders}
                        type="button"
                      >
                        Refresh
                      </button>
                    </div>
                    <div className="pharmacy-pos__orders-table">
                      <Table className="pharmacy-pos__table" responsive>
                        <thead>
                          <tr>
                            <th>Order</th>
                            <th>Customer</th>
                            <th>Status</th>
                            <th>Total</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {otherOrdersLoading && (
                            <tr>
                              <td colSpan="5">Loading orders...</td>
                            </tr>
                          )}
                          {!otherOrdersLoading && otherOrders.length === 0 && (
                            <tr>
                              <td colSpan="5">No matching district orders</td>
                            </tr>
                          )}
                          {!otherOrdersLoading &&
                            otherOrders.map((order) => {
                              const isSelected =
                                selectedOtherOrder &&
                                selectedOtherOrder.id === order.id;

                              return (
                                <tr
                                  className={
                                    isSelected
                                      ? "pharmacy-pos__table-row--active"
                                      : ""
                                  }
                                  key={order.id}
                                  onClick={() => selectOtherOrder(order)}
                                >
                                  <td>
                                    <strong>
                                      {order.opharma_reference || order.id}
                                    </strong>
                                    <span className="pharmacy-pos__table-subtext">
                                      {order.order_display_date ||
                                        order.created_at}
                                    </span>
                                  </td>
                                  <td>
                                    {orderCustomerName(order)}
                                    <span className="pharmacy-pos__table-subtext">
                                      {orderCustomerMobile(order)}
                                    </span>
                                  </td>
                                  <td>
                                    {order.status_label ||
                                      order.order_status_label}
                                  </td>
                                  <td>{currency(order.total_cost)}</td>
                                  <td>
                                    <button
                                      className={
                                        isSelected
                                          ? "pharmacy-pos__small-button pharmacy-pos__small-button--selected"
                                          : "pharmacy-pos__small-button"
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        selectOtherOrder(order);
                                      }}
                                      type="button"
                                    >
                                      {isSelected ? "Selected" : "Open"}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </div>
                  </div>

                  <div className="pharmacy-pos__panel pharmacy-pos__order-detail-panel">
                    {!selectedOtherOrder && (
                      <div className="pharmacy-pos__order-placeholder">
                        <strong>No order selected</strong>
                      </div>
                    )}

                    {selectedOtherOrder && (
                      <>
                        <div className="pharmacy-pos__order-detail-header">
                          <div>
                            <div className="pharmacy-pos__panel-title">
                              Order{" "}
                              {selectedOtherOrder.opharma_reference ||
                                selectedOtherOrder.id}
                            </div>
                            <div className="pharmacy-pos__muted">
                              {orderCustomerName(selectedOtherOrder)} -{" "}
                              {orderCustomerMobile(selectedOtherOrder)}
                            </div>
                          </div>
                          <span className="pharmacy-pos__status-pill">
                            {selectedOtherOrder.status_label ||
                              selectedOtherOrder.order_status_label ||
                              "Pending"}
                          </span>
                        </div>

                        <div className="pharmacy-pos__order-summary-grid">
                          <div>
                            <span>Total</span>
                            <strong>
                              {currency(selectedOtherOrder.total_cost)}
                            </strong>
                          </div>
                          <div>
                            <span>Created</span>
                            <strong>
                              {selectedOtherOrder.order_display_date ||
                                selectedOtherOrder.created_at ||
                                "-"}
                            </strong>
                          </div>
                          <div>
                            <span>Items</span>
                            <strong>{otherOrderCart.length}</strong>
                          </div>
                        </div>

                        <div className="pharmacy-pos__order-detail-grid">
                          <section className="pharmacy-pos__order-section">
                            <div className="pharmacy-pos__section-heading">
                              Prescription
                            </div>
                            {prescriptionFilesForOrder(selectedOtherOrder)
                              .length === 0 &&
                              !prescriptionTextForOrder(
                                selectedOtherOrder,
                              ) && (
                                <div className="pharmacy-pos__empty">
                                  No prescription attached
                                </div>
                              )}
                            {prescriptionFilesForOrder(
                              selectedOtherOrder,
                            ).map((file) => (
                              <a
                                href={file}
                                key={file}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <img
                                  alt="Prescription"
                                  className="pharmacy-pos__prescription-image"
                                  src={file}
                                />
                              </a>
                            ))}
                            {prescriptionTextForOrder(selectedOtherOrder) && (
                              <pre className="pharmacy-pos__prescription-text">
                                {prescriptionTextForOrder(selectedOtherOrder)}
                              </pre>
                            )}
                          </section>

                          <section className="pharmacy-pos__order-section">
                            <div className="pharmacy-pos__section-heading">
                              Add Medicines
                            </div>
                            <input
                              className="pharmacy-pos__search pharmacy-pos__search--full"
                              onChange={(event) =>
                                setOtherOrderSearch(event.target.value)
                              }
                              placeholder="Search store medicine"
                              value={otherOrderSearch}
                            />
                            <div className="pharmacy-pos__medicine-results">
                              <Table className="pharmacy-pos__table" responsive>
                                <thead>
                                  <tr>
                                    <th>Medicine</th>
                                    <th>Stock</th>
                                    <th>Price</th>
                                    <th />
                                  </tr>
                                </thead>
                                <tbody>
                                  {otherOrderProducts.map((product) => (
                                    <tr key={product.id}>
                                      <td>{product.name}</td>
                                      <td>{quantityForProduct(product)}</td>
                                      <td>{currency(product.selling_price)}</td>
                                      <td>
                                        <button
                                          className="pharmacy-pos__small-button"
                                          disabled={
                                            quantityForProduct(product) <= 0
                                          }
                                          onClick={() =>
                                            addProductToOtherOrder(product)
                                          }
                                          type="button"
                                        >
                                          Add
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </section>
                        </div>

                        <section className="pharmacy-pos__order-section pharmacy-pos__order-section--cart">
                          <div className="pharmacy-pos__section-heading">
                            Order Medicines
                          </div>
                          {otherOrderCart.length === 0 && (
                            <div className="pharmacy-pos__empty">
                              No medicines selected
                            </div>
                          )}
                          {otherOrderCart.map((item) => (
                            <div
                              className="pharmacy-pos__cart-line pharmacy-pos__cart-line--order"
                              key={item.medication_id}
                            >
                              <div className="pharmacy-pos__cart-name">
                                <strong>{item.name}</strong>
                                <span>{currency(item.unit_price)}</span>
                              </div>
                              <div className="pharmacy-pos__qty pharmacy-pos__qty--single">
                                <input
                                  min="1"
                                  onChange={(event) =>
                                    updateOtherOrderItem(
                                      item.medication_id,
                                      "quantity",
                                      event.target.value,
                                    )
                                  }
                                  type="number"
                                  value={item.quantity}
                                />
                              </div>
                              <strong>
                                {currency(
                                  Number(item.unit_price || 0) *
                                    Number(item.quantity || 0),
                                )}
                              </strong>
                            </div>
                          ))}
                          <div className="pharmacy-pos__order-submit-bar">
                            <div className="pharmacy-pos__total-line">
                              <span>Total</span>
                              <strong>
                                {currency(
                                  otherOrderCart.reduce(
                                    (sum, item) =>
                                      sum +
                                      Number(item.unit_price || 0) *
                                        Number(item.quantity || 0),
                                    0,
                                  ),
                                )}
                              </strong>
                            </div>
                            <button
                              className="pharmacy-pos__button"
                              disabled={
                                otherOrderSubmitting ||
                                otherOrderCart.length === 0
                              }
                              onClick={submitOtherOrder}
                              type="button"
                            >
                              {otherOrderSubmitting
                                ? "Submitting"
                                : "Submit Order"}
                            </button>
                          </div>
                        </section>
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "reports" && (
                <>
                  <div className="pharmacy-pos__report-filter">
                    <label>
                      From
                      <input
                        onChange={(event) => setReportFrom(event.target.value)}
                        type="date"
                        value={reportFrom}
                      />
                    </label>
                    <label>
                      To
                      <input
                        onChange={(event) => setReportTo(event.target.value)}
                        type="date"
                        value={reportTo}
                      />
                    </label>
                    <button
                      className="pharmacy-pos__small-button"
                      onClick={loadReport}
                      type="button"
                    >
                      Apply
                    </button>
                  </div>
                  <div className="pharmacy-pos__metrics">
                    <div className="pharmacy-pos__metric">
                      <span>Total sales</span>
                      <strong>{currency(report.grand_total)}</strong>
                    </div>
                    <div className="pharmacy-pos__metric">
                      <span>Gross profit</span>
                      <strong>{currency(report.gross_profit)}</strong>
                    </div>
                    <div className="pharmacy-pos__metric">
                      <span>Profit margin</span>
                      <strong>{Number(report.profit_margin || 0)}%</strong>
                    </div>
                    <div className="pharmacy-pos__metric">
                      <span>Transactions</span>
                      <strong>{report.sales_count || 0}</strong>
                    </div>
                  </div>

                  <Row>
                    <Col md={4}>
                      <div className="pharmacy-pos__panel">
                        <div className="pharmacy-pos__panel-header">
                          <div className="pharmacy-pos__panel-title">
                            Payment split
                          </div>
                        </div>
                        <Table className="pharmacy-pos__table" responsive>
                          <tbody>
                            {(report.payments || []).map((item) => (
                              <tr key={item.method}>
                                <td>{item.method}</td>
                                <td>{currency(item.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="pharmacy-pos__panel">
                        <div className="pharmacy-pos__panel-header">
                          <div className="pharmacy-pos__panel-title">
                            Top products
                          </div>
                        </div>
                        <Table className="pharmacy-pos__table" responsive>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Qty</th>
                              <th>Sales</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(report.top_products || []).map((item) => (
                              <tr
                                key={item.pos_product_id || item.product_name}
                              >
                                <td>{item.product_name}</td>
                                <td>{item.quantity_sold}</td>
                                <td>{currency(item.sales_total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="pharmacy-pos__panel">
                        <div className="pharmacy-pos__panel-header">
                          <div className="pharmacy-pos__panel-title">
                            Attention needed
                          </div>
                        </div>
                        <Table className="pharmacy-pos__table" responsive>
                          <tbody>
                            {(report.low_stock || []).map((item) => (
                              <tr key={`low-${item.id}`}>
                                <td className="pharmacy-pos__danger">
                                  Low stock
                                </td>
                                <td>{item.name}</td>
                                <td>{item.quantity_on_hand}</td>
                              </tr>
                            ))}
                            {(report.expiring_soon || []).map((item) => (
                              <tr key={`exp-${item.id}`}>
                                <td className="pharmacy-pos__warning">
                                  Expiring
                                </td>
                                <td>{item.product_name}</td>
                                <td>{item.quantity_on_hand}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

export default PharmacyPos;
